'use client';

import { useState } from 'react';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderDayColumn, ARRIVAL_ORDER_COLLAPSED_LIMIT } from './arrival-order-day-column.component';

import { Button } from '@/components/ui/button/button.component';
import {
  SlotsNextArrow,
  SlotsPrevArrow,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/slots-pagination-arrows.component';
import { SlotsSkeleton } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/slots-skeleton.component';
import { useScheduleSlots } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_hooks/use-schedule-slots.hook';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { dateFormatter } from '@/utils/formatters/date';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ArrivalOrderSlotsGridProps {
  scheduleId: string | undefined;
  limitDays: number;
  partnerId: string;
  price: number;
  onSeeMore?: () => void;
  slotClassName?: string;
}

export function ArrivalOrderSlotsGrid({
  scheduleId,
  limitDays,
  partnerId,
  price,
  onSeeMore,
  slotClassName,
}: ArrivalOrderSlotsGridProps) {
  const { isLoading, days, hasMore, offset, goNext, goPrev } = useScheduleSlots({
    scheduleId,
    limitDays,
  });
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const analytics = getAnalyticsPlugin();

  const toggleExpand = (date: string) => {
    setExpandedDate((current) => (current === date ? null : date));
  };

  const handleSlotClick = (slot: SchedulesSearchSlot) => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_SLOT, {
      partner_id: partnerId,
      data: slot.date,
      faixa_horario: `${dateFormatter(slot.start, 'HH:mm')} às ${dateFormatter(slot.end, 'HH:mm')}`,
      price,
    });
  };

  const handlePrev = () => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_DATE_NAV, { partner_id: partnerId, direcao: 'anterior' });
    goPrev();
  };

  const handleNext = () => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_DATE_NAV, { partner_id: partnerId, direcao: 'proxima' });
    goNext();
  };

  if (isLoading) {
    return <SlotsSkeleton variant="modal" columns={limitDays} />;
  }

  if (days.length === 0) {
    return (
      <Typography variant={TypographyVariant.BODY_TEXT} className="text-center text-gray-500 py-6">
        Nenhum horário disponível.
      </Typography>
    );
  }

  const canSeeMore = Boolean(onSeeMore)
    && days.some((day) => day.slots.length > ARRIVAL_ORDER_COLLAPSED_LIMIT);

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-start justify-center w-full">
        <SlotsPrevArrow
          disabled={offset === 0}
          testId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_PREVIOUS_ARROW_BUTTON}
          onClick={handlePrev}
        />
        <div className="flex-1 flex gap-4 items-start min-w-0">
          {days.map((day) => (
            <ArrivalOrderDayColumn
              key={day.date}
              date={day.date}
              slots={day.slots}
              expanded={expandedDate === day.date}
              slotClassName={slotClassName}
              onToggleExpand={() => toggleExpand(day.date)}
              onSlotClick={handleSlotClick}
            />
          ))}
        </div>
        <SlotsNextArrow
          disabled={!hasMore}
          testId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_NEXT_ARROW_BUTTON}
          onClick={handleNext}
        />
      </div>
      {canSeeMore && (
        <Button
          variant="text"
          color="primary"
          startIcon={faPlus}
          startIconSize="xs"
          startIconClassName="text-xs text-green-500"
          className="self-center gap-2 py-1"
          data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_OPEN_MODAL}
          onClick={onSeeMore}
        >
          Ver outros horários
        </Button>
      )}
    </div>
  );
}
